// ============================================================================
// Tasks data layer (Phase 1).
//
// Mirrors the sub-replacement / supply-management hook pattern: list hook with
// optimistic write helpers, plus form-option hooks for facility / employee
// pickers. No TanStack Query yet — sister modules don't use it and this build
// follows that convention.
//
// task_id is auto-assigned by the BEFORE INSERT trigger as FTK-NNNN.
// Frontends MUST NOT supply it. The trigger also defaults status/priority/source.
//
// RLS guarantees scope:
//   - AMs see all rows (am_read_all) but can only INSERT/UPDATE rows on
//     facilities they own (is_my_facility(facility_id)).
//   - Finance / HR read-only.
//   - Superuser unconstrained.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { errorMessage } from '../../../shared/errors';
import { localToday } from '../../../shared/format';
import type {
  FacilityTask,
  TaskCategory,
  TaskComment,
  TaskPriority,
  TaskSource,
  TaskStatus,
} from '../../../shared/types';

export interface FacilityTaskRow extends FacilityTask {
  facility_name: string | null;
  market_region: string | null;
  account_name: string | null;
  assignee_first: string | null;
  assignee_last: string | null;
}

interface FacilityTaskRowFromDb extends FacilityTask {
  facilities: {
    facility_name: string | null;
    market_region: string | null;
  } | null;
  accounts: { account_name: string | null } | null;
  assignee: {
    employee_name_first: string | null;
    employee_name_last: string | null;
  } | null;
}

const TASK_SELECT = `
  task_id, facility_id, account_id, description, priority, status, category,
  created_by, assigned_to, due_date, source, related_feedback_id,
  completion_notes, completed_date, completed_by, created_date, updated_date,
  comments,
  facilities ( facility_name, market_region ),
  accounts   ( account_name ),
  assignee:employees!facility_tasks_assigned_to_fkey (
    employee_name_first, employee_name_last
  )
`;

function flatten(rows: FacilityTaskRowFromDb[]): FacilityTaskRow[] {
  return rows.map((r) => ({
    task_id: r.task_id,
    facility_id: r.facility_id,
    account_id: r.account_id,
    description: r.description,
    priority: r.priority,
    status: r.status,
    category: r.category ?? null,
    created_by: r.created_by,
    assigned_to: r.assigned_to,
    due_date: r.due_date,
    source: r.source,
    related_feedback_id: r.related_feedback_id,
    completion_notes: r.completion_notes,
    completed_date: r.completed_date,
    completed_by: r.completed_by,
    created_date: r.created_date,
    updated_date: r.updated_date,
    comments: (r.comments as TaskComment[] | null) ?? [],
    facility_name: r.facilities?.facility_name ?? null,
    market_region: r.facilities?.market_region ?? null,
    account_name: r.accounts?.account_name ?? null,
    assignee_first: r.assignee?.employee_name_first ?? null,
    assignee_last: r.assignee?.employee_name_last ?? null,
  }));
}

export interface TaskFilter {
  facilityId?: string | null;
  statuses?: TaskStatus[];
  priorities?: TaskPriority[];
  // Single-assignee filter (legacy). If both `assignedTo` and
  // `assigneeIncludesUnassigned` are set, the query matches rows where the
  // assignee is the given employee OR the row is unassigned. This is the
  // "Me OR Unassigned" pattern used by the role-aware default view.
  assignedTo?: string | null;
  assigneeIncludesUnassigned?: boolean;
  sources?: TaskSource[];
  categories?: TaskCategory[];
}

export type TaskSortKey = 'priority_due' | 'created' | 'facility';

const PRIORITY_RANK: Record<TaskPriority, number> = {
  High: 0,
  Medium: 1,
  Low: 2,
};

function sortRows(rows: FacilityTaskRow[], key: TaskSortKey): FacilityTaskRow[] {
  const copy = [...rows];
  if (key === 'created') {
    copy.sort((a, b) =>
      (b.created_date ?? '').localeCompare(a.created_date ?? ''),
    );
  } else if (key === 'facility') {
    copy.sort((a, b) =>
      (a.facility_name ?? '').localeCompare(b.facility_name ?? ''),
    );
  } else {
    copy.sort((a, b) => {
      const pa = PRIORITY_RANK[a.priority] ?? 99;
      const pb = PRIORITY_RANK[b.priority] ?? 99;
      if (pa !== pb) return pa - pb;
      const da = a.due_date ?? '9999-12-31';
      const db = b.due_date ?? '9999-12-31';
      if (da !== db) return da.localeCompare(db);
      return (a.created_date ?? '').localeCompare(b.created_date ?? '');
    });
  }
  return copy;
}

export function useTasks(filter: TaskFilter, sort: TaskSortKey = 'priority_due'): {
  data: FacilityTaskRow[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refetch: () => void;
  updateStatus: (
    taskId: string,
    next: TaskStatus,
    extra?: { completed_by?: string | null; completion_notes?: string | null },
  ) => Promise<void>;
} {
  const [data, setData] = useState<FacilityTaskRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [tick, setTick] = useState(0);

  const filterKey = JSON.stringify(filter);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading((prev) => (lastUpdated === null ? true : prev));
      setError(null);
      try {
        let q = supabase.from('facility_tasks').select(TASK_SELECT);
        if (filter.facilityId) q = q.eq('facility_id', filter.facilityId);
        if (filter.statuses && filter.statuses.length > 0) {
          q = q.in('status', filter.statuses);
        }
        if (filter.priorities && filter.priorities.length > 0) {
          q = q.in('priority', filter.priorities);
        }
        // Assignee filter — three shapes:
        //   { assignedTo: 'EID-X' } → exact assignee match
        //   { assignedTo: 'EID-X', assigneeIncludesUnassigned: true } → EID-X OR null
        //   { assigneeIncludesUnassigned: true } (no assignedTo) → null only
        if (filter.assignedTo && filter.assigneeIncludesUnassigned) {
          q = q.or(
            `assigned_to.eq.${filter.assignedTo},assigned_to.is.null`,
          );
        } else if (filter.assignedTo) {
          q = q.eq('assigned_to', filter.assignedTo);
        } else if (filter.assigneeIncludesUnassigned) {
          q = q.is('assigned_to', null);
        }
        if (filter.sources && filter.sources.length > 0) {
          q = q.in('source', filter.sources);
        }
        if (filter.categories && filter.categories.length > 0) {
          q = q.in('category', filter.categories);
        }
        const { data: rows, error: e } = await q.order('created_date', {
          ascending: false,
        });
        if (cancelled) return;
        if (e) throw e;
        const flat = flatten((rows ?? []) as unknown as FacilityTaskRowFromDb[]);
        setData(sortRows(flat, sort));
        setLastUpdated(new Date());
      } catch (err) {
        if (cancelled) return;
        console.error('[tasks] load failed', err);
        setError(
          errorMessage(err, 'Failed to load tasks.', 'tasks:list'),
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, filterKey, sort]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  const updateStatus = useCallback(
    async (
      taskId: string,
      next: TaskStatus,
      extra: { completed_by?: string | null; completion_notes?: string | null } = {},
    ) => {
      let prev: FacilityTaskRow | null = null;
      setData((rows) =>
        rows.map((r) => {
          if (r.task_id !== taskId) return r;
          prev = r;
          return {
            ...r,
            status: next,
            completed_by:
              next === 'Done' ? (extra.completed_by ?? r.completed_by) : null,
            completed_date:
              next === 'Done' ? new Date().toISOString().slice(0, 10) : null,
            completion_notes:
              extra.completion_notes !== undefined
                ? extra.completion_notes
                : r.completion_notes,
          };
        }),
      );
      try {
        const payload: Record<string, unknown> = {
          status: next,
          updated_date: new Date().toISOString().slice(0, 10),
        };
        if (next === 'Done') {
          payload.completed_by = extra.completed_by ?? null;
          payload.completed_date = new Date().toISOString().slice(0, 10);
        } else {
          payload.completed_by = null;
          payload.completed_date = null;
        }
        if (extra.completion_notes !== undefined) {
          payload.completion_notes = extra.completion_notes;
        }
        const { error: e } = await supabase
          .from('facility_tasks')
          .update(payload)
          .eq('task_id', taskId);
        if (e) throw e;
      } catch (err) {
        console.error('[tasks] status update failed', err);
        if (prev) {
          const restore = prev;
          setData((rows) =>
            rows.map((r) => (r.task_id === taskId ? restore : r)),
          );
        }
        throw err;
      }
    },
    [],
  );

  return useMemo(
    () => ({ data, isLoading, error, lastUpdated, refetch, updateStatus }),
    [data, isLoading, error, lastUpdated, refetch, updateStatus],
  );
}

export interface NewTaskInput {
  /** Null for a task that is not about one building (no-building.ts). */
  facility_id: string | null;
  account_id: string | null;
  description: string;
  priority: TaskPriority;
  status?: TaskStatus;
  category?: TaskCategory | null;
  due_date?: string | null;
  assigned_to: string | null;
  created_by: string;
  source?: TaskSource;
  related_feedback_id?: string | null;
  completion_notes?: string | null;
}

export async function createTask(input: NewTaskInput): Promise<FacilityTask> {
  const payload = {
    facility_id: input.facility_id,
    account_id: input.account_id,
    description: input.description,
    priority: input.priority,
    status: input.status ?? 'Open',
    category: input.category ?? 'Operations',
    due_date: input.due_date ?? null,
    assigned_to: input.assigned_to,
    created_by: input.created_by,
    source: input.source ?? 'Manual',
    related_feedback_id: input.related_feedback_id ?? null,
    completion_notes: input.completion_notes ?? null,
  };
  const { data, error } = await supabase
    .from('facility_tasks')
    .insert(payload)
    .select(
      'task_id, facility_id, account_id, description, priority, status, category, due_date, assigned_to, created_by, source, related_feedback_id, completion_notes, completed_date, completed_by, created_date, updated_date, comments',
    )
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('Insert succeeded but no row returned.');
  return data as FacilityTask;
}

// Append a comment to facility_tasks.comments (AFB-0012). Read-modify-write —
// comment volume is low (a 1–2 message exchange per task). The comment-write
// rides the same facility_tasks_self_update / am_update_tasks UPDATE policies
// that already gate the row. Returns the appended comment.
export async function addComment(
  taskId: string,
  body: string,
  authorEid: string,
): Promise<TaskComment> {
  const { data: row, error: selErr } = await supabase
    .from('facility_tasks')
    .select('comments')
    .eq('task_id', taskId)
    .maybeSingle();
  if (selErr) throw selErr;
  const existing = ((row?.comments as TaskComment[] | null) ?? []);
  const comment: TaskComment = {
    comment_id: `c_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    author_eid: authorEid,
    body,
    created_at: new Date().toISOString(),
  };
  const { data: saved, error } = await supabase
    .from('facility_tasks')
    .update({
      comments: [...existing, comment],
      updated_date: localToday(),
    })
    .eq('task_id', taskId)
    .select('task_id');
  if (error) throw error;
  // A refused UPDATE is 0 rows and no error — say so rather than "posted".
  if (!saved || saved.length === 0) throw new Error(NOT_SAVED);
  return comment;
}

export type TaskEditInput = Partial<
  Pick<
    FacilityTask,
    | 'description'
    | 'priority'
    | 'due_date'
    | 'assigned_to'
    | 'status'
    | 'category'
    | 'completion_notes'
    | 'completed_by'
    | 'completed_date'
  >
>;

export async function updateTask(
  taskId: string,
  patch: TaskEditInput,
): Promise<void> {
  const payload: Record<string, unknown> = {
    ...patch,
    updated_date: localToday(),
  };
  const { data: saved, error } = await supabase
    .from('facility_tasks')
    .update(payload)
    .eq('task_id', taskId)
    .select('task_id');
  if (error) throw error;
  // A refused UPDATE is 0 rows and no error, so the screen said "saved" when
  // nothing changed (only the assignee, the creator, the building's AM or a
  // superuser may change a task — facility_tasks RLS).
  if (!saved || saved.length === 0) throw new Error(NOT_SAVED);
}

const NOT_SAVED = "That didn't save — only the person it is assigned to, the person who made it, the building's account manager or a superuser can change this task.";

// ── The task's own buttons, ONE copy each (the task screen and Ask AI's task
// card call these; item #944, the tasks part) ─────────────────────────────

/** Resolve = close with a required note. The note is written to
 *  completion_notes AND posted as the last comment so the creator sees it. */
export async function resolveTask(
  taskId: string,
  status: 'Done' | 'Deferred',
  note: string,
  byEid: string,
): Promise<void> {
  await addComment(taskId, note, byEid);
  await updateTask(taskId, {
    status,
    completion_notes: note,
    completed_by: status === 'Done' ? byEid : null,
    completed_date: status === 'Done' ? localToday() : null,
  });
}

/** Reopen a Done or Deferred task: Open again, no longer completed. */
export async function reopenTask(taskId: string): Promise<void> {
  await updateTask(taskId, { status: 'Open', completed_by: null, completed_date: null });
}

/** Send a task back to the person who made it, with the reason as a comment. */
export async function sendTaskBack(
  task: { task_id: string; created_by: string },
  reason: string,
  creatorLabel: string,
  byEid: string,
): Promise<void> {
  await addComment(task.task_id, `Sent back to ${creatorLabel}: ${reason}`, byEid);
  await updateTask(task.task_id, { assigned_to: task.created_by });
}

export function useFacilityTasks(facilityId: string | null): {
  active: FacilityTaskRow[];
  closed: FacilityTaskRow[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  updateStatus: (
    taskId: string,
    next: TaskStatus,
    extra?: { completed_by?: string | null; completion_notes?: string | null },
  ) => Promise<void>;
  prependInserted: (row: FacilityTask) => void;
  replaceUpdated: (row: FacilityTask) => void;
} {
  const filter = useMemo<TaskFilter>(
    () => ({ facilityId: facilityId ?? null }),
    [facilityId],
  );
  const enabled = Boolean(facilityId);
  const inner = useTasks(filter, 'priority_due');

  const [localOverrides, setLocalOverrides] = useState<FacilityTaskRow[]>([]);

  const active = useMemo(() => {
    if (!enabled) return [];
    const merged = mergeOverrides(inner.data, localOverrides);
    return sortRows(
      merged.filter((t) => t.status === 'Open' || t.status === 'In_Progress'),
      'priority_due',
    );
  }, [enabled, inner.data, localOverrides]);

  const closed = useMemo(() => {
    if (!enabled) return [];
    const merged = mergeOverrides(inner.data, localOverrides);
    return merged
      .filter((t) => t.status === 'Done' || t.status === 'Deferred')
      .sort((a, b) =>
        (b.completed_date ?? b.updated_date ?? '').localeCompare(
          a.completed_date ?? a.updated_date ?? '',
        ),
      );
  }, [enabled, inner.data, localOverrides]);

  const prependInserted = useCallback((row: FacilityTask) => {
    const enriched: FacilityTaskRow = {
      ...row,
      facility_name: null,
      market_region: null,
      account_name: null,
      assignee_first: null,
      assignee_last: null,
    };
    setLocalOverrides((rows) => [enriched, ...rows]);
  }, []);

  const replaceUpdated = useCallback((row: FacilityTask) => {
    setLocalOverrides((rows) => {
      const others = rows.filter((r) => r.task_id !== row.task_id);
      const enriched: FacilityTaskRow = {
        ...row,
        facility_name: null,
        market_region: null,
        account_name: null,
        assignee_first: null,
        assignee_last: null,
      };
      return [enriched, ...others];
    });
  }, []);

  return {
    active,
    closed,
    isLoading: inner.isLoading,
    error: inner.error,
    refetch: inner.refetch,
    updateStatus: inner.updateStatus,
    prependInserted,
    replaceUpdated,
  };
}

function mergeOverrides(
  base: FacilityTaskRow[],
  overrides: FacilityTaskRow[],
): FacilityTaskRow[] {
  if (overrides.length === 0) return base;
  const byId = new Map<string, FacilityTaskRow>(base.map((r) => [r.task_id, r]));
  for (const o of overrides) {
    const existing = byId.get(o.task_id);
    byId.set(o.task_id, {
      ...o,
      facility_name: o.facility_name ?? existing?.facility_name ?? null,
      market_region: o.market_region ?? existing?.market_region ?? null,
      account_name: o.account_name ?? existing?.account_name ?? null,
      assignee_first: o.assignee_first ?? existing?.assignee_first ?? null,
      assignee_last: o.assignee_last ?? existing?.assignee_last ?? null,
    });
  }
  return Array.from(byId.values());
}

export function useTaskById(taskId: string | null): {
  data: FacilityTaskRow | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const [data, setData] = useState<FacilityTaskRow | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!taskId) {
      setData(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data: row, error: e } = await supabase
          .from('facility_tasks')
          .select(TASK_SELECT)
          .eq('task_id', taskId)
          .maybeSingle();
        if (cancelled) return;
        if (e) throw e;
        setData(
          row
            ? flatten([row as unknown as FacilityTaskRowFromDb])[0] ?? null
            : null,
        );
      } catch (err) {
        if (cancelled) return;
        console.error('[tasks] detail load failed', err);
        setError(
          errorMessage(err, 'Failed to load the task.', 'tasks:detail'),
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [taskId, tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  return { data, isLoading, error, refetch };
}
